import {
  DynamicTableFrameExtraProps,
  FrameProps,
  TableFrame,
} from './TableFrame';
import {
  DynamicModel,
  WithDimensionsReceivedProps,
  WithDimensionsSentProps,
  BasicDynamicModelEditor,
  Expression,
  ExpressionInput,
} from '@k3/core';
import {
  DefaultModelProps,
  Model,
} from '@k3/core/dist/src/lib/types/model.interface';
import { FormControlLabel, Stack, Switch } from '@mui/material';
import { ModelSelector } from './ModelSelector';
import AxisSelect from './AxisSelect';
import { memo } from 'react';

export const getFrameSubmodelKeys = (model: Model) => {
  const props = model?.props as FrameProps;

  return [
    ...(props?.tableLegKeys ?? []),
    ...(props?.middleTableKeys ?? []),
    ...(props?.connectorKeys ?? []),
    ...(props?.mirroredTableLegKeys ?? []),
  ];
};

type DynamicFrameReceivedProps = {
  startOffset: number;
  endOffset: number;
};

type DynamicFrameSentProps = {
  startOffset: Expression;
  endOffset: Expression;
};

type DynamicTableFrameProps = WithDimensionsReceivedProps &
  DynamicTableFrameExtraProps &
  DynamicFrameReceivedProps &
  DefaultModelProps &
  Pick<Model, 'id'>;

const DynamicTableFrame = memo((props: DynamicTableFrameProps) => {
  return (
    <group
      userData={{
        modelId: props.id,
      }}
      position={props.position}
      rotation={props.rotation}
    >
      <TableFrame
        {...props}
        width={props.width}
        height={props.height}
        depth={props.depth}
      />
    </group>
  );
});

export const tableFrame: DynamicModel<
  DynamicTableFrameProps,
  WithDimensionsSentProps & DynamicTableFrameExtraProps & DynamicFrameSentProps
> = {
  type: 'tabelFrame',
  label: 'Tischgestell',
  disabledForAR: false,
  component: memo((props) => {
    return <DynamicTableFrame {...props} />;
  }),
  propsDialog: (props) => {
    return (
      <Stack
        direction="column"
        spacing={4}
        sx={{
          width: '100%',
          maxWidth: 450,
          overflowX: 'hidden',
        }}
      >
        <BasicDynamicModelEditor {...props} materialsAreEditable={false} />

        <AxisSelect
          axis={props.dynamicModelProps.axis}
          onChange={props.onChange('axis')}
        />

        <FormControlLabel
          control={
            <Switch
              checked={props.dynamicModelProps.renderLeftCap}
              onChange={(event) =>
                props.onChange('renderLeftCap')(event.target.checked)
              }
            />
          }
          label="Linkes Bein anzeigen"
        />
        <FormControlLabel
          control={
            <Switch
              checked={props.dynamicModelProps.renderRightCap}
              onChange={(event) =>
                props.onChange('renderRightCap')(event.target.checked)
              }
            />
          }
          label="Rechtes Bein anzeigen"
        />

        <ExpressionInput
          label="Zarge Extra Startabstand"
          value={props.dynamicModelProps.startOffset.expression}
          onChange={(expression) =>
            props.onChange('startOffset')({ expression })
          }
        />
        <ExpressionInput
          label="Zarge Extra Endabstand"
          value={props.dynamicModelProps.endOffset.expression}
          onChange={(expression) => props.onChange('endOffset')({ expression })}
        />

        <ModelSelector
          label={'Tischbeine'}
          multiple
          modelKey={props.dynamicModelProps.tableLegKeys ?? []}
          onChangeMultiple={props.onChange('tableLegKeys')}
        />
        <ModelSelector
          label={'Tischbeine (gespiegelt)'}
          multiple
          modelKey={props.dynamicModelProps.mirroredTableLegKeys ?? []}
          onChangeMultiple={props.onChange('mirroredTableLegKeys')}
        />
        <ModelSelector
          label={'Mittelteile'}
          multiple
          modelKey={props.dynamicModelProps.middleTableKeys ?? []}
          onChangeMultiple={props.onChange('middleTableKeys')}
        />
        <ModelSelector
          label={'Verbinder'}
          multiple
          modelKey={props.dynamicModelProps.connectorKeys ?? []}
          onChangeMultiple={props.onChange('connectorKeys')}
        />
      </Stack>
    );
  },
  defaultProps: {
    startOffset: { expression: '0' },
    endOffset: { expression: '0' },
    width: { expression: '1' },
    height: { expression: '1' },
    depth: { expression: '1' },
    tableLegKeys: [],
    mirroredTableLegKeys: [],
    middleTableKeys: [],
    connectorKeys: [],
    middleLengthOffset: 0,
    renderLeftCap: true,
    renderRightCap: true,
    axis: 'x',
  },
  materials: [],
};
